import { FunctionComponent, useCallback, useMemo, useRef, useState } from "react"
import { useMissionLogsHeatmap } from "./PilotQueries"
import { PilotHistoryTabSwitcher } from "./PilotHistoryTabSwitcher"
import { DateFilter } from "../filters/DateFilter"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectDateFilter } from "../redux/missions-table-selectors"
import { setDateFilter } from "../redux/missions-table-slice"
import { fitMapToBounds } from "features/map/MapPreview"
import { Map, Source, Layer, MapRef, StyleSpecification } from "react-map-gl/dist/maplibre"
import type { HeatmapLayerSpecification } from "maplibre-gl"
import { useMapStylesJSON } from "features/map/control/selectors"
import { maxZoom } from "@nova/constants"
import { ButtonSecondary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"

interface PilotMapViewProps {
    userId: string
}

const heatmapLayerStyle: HeatmapLayerSpecification = {
    id: "flights-heatmap",
    type: "heatmap",
    paint: {
        "heatmap-weight": ["interpolate", ["linear"], ["get", "weight"], 0, 0, 1, 1],
        "heatmap-intensity": 0.5,
        "heatmap-color": [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0.0,
            "rgba(144, 238, 144, 0)",
            0.1,
            "rgba(144, 238, 144, 1)",
            0.4,
            "rgb(255, 255, 0)",
            0.6,
            "rgb(255, 200, 0)",
            0.8,
            "rgb(255, 100, 0)",
            0.95,
            "rgb(200, 0, 0)",
            1.0,
            "rgb(139, 0, 0)",
        ],
        "heatmap-radius": 35,
        "heatmap-opacity": 0.8,
    },

    source: "flights-source",
}
export const PilotMapView: FunctionComponent<PilotMapViewProps> = ({ userId }) => {
    const dispatch = useAppDispatch()
    const [isZoomed, setIsZoomed] = useState(true)
    const mapRef = useRef<MapRef | null>(null)
    const mapStylesJSON = useMapStylesJSON()

    const mapStyle = useMemo(
        () => ({ ...mapStylesJSON, terrain: undefined }) as StyleSpecification,
        [mapStylesJSON]
    )

    const dateFilter = useAppSelector(selectDateFilter)

    const { data } = useMissionLogsHeatmap({
        userId,
        from: dateFilter?.from,
        to: dateFilter?.to,
    })

    const geojsonData = useMemo(() => {
        if (!data) return null
        return {
            type: "FeatureCollection" as const,
            features: data.map((d) => ({
                type: "Feature" as const,
                geometry: {
                    type: "Point" as const,
                    coordinates: [d.lng, d.lat],
                },
                properties: {
                    weight: d.weight,
                },
            })),
        }
    }, [data])

    const zoomToFlights = useCallback(() => {
        if (!mapRef.current || !data) return
        fitMapToBounds(
            mapRef.current,
            data.map((d) => [d.lng, d.lat])
        )
        setIsZoomed(true)
    }, [data])

    return (
        <div className="flex flex-col gap-4 h-full pb-12">
            <div className="flex items-center justify-between py-0.5">
                <DateFilter
                    value={dateFilter ?? undefined}
                    onChange={(v) => dispatch(setDateFilter(v ?? null))}
                />
                <PilotHistoryTabSwitcher />
            </div>

            <div className="flex-1 w-full h-full rounded-lg overflow-hidden">
                {!!geojsonData && (
                    <Map
                        ref={mapRef}
                        mapStyle={mapStyle}
                        initialViewState={{ latitude: 0, longitude: 0, zoom: 1 }}
                        maxZoom={maxZoom}
                        attributionControl={false}
                        onLoad={zoomToFlights}
                        onMoveStart={() => setIsZoomed(false)}
                        style={{ width: "100%", height: "100%", position: "relative" }}
                    >
                        {!isZoomed && (
                            <div className="absolute bottom-4 right-4 z-20">
                                <ButtonSecondary
                                    title="Zoom To Flights"
                                    LeftIcon={Icons.ZoomTo}
                                    onClick={zoomToFlights}
                                />
                            </div>
                        )}

                        <Source id="flights-source" type="geojson" data={geojsonData}>
                            <Layer {...heatmapLayerStyle} />
                        </Source>
                    </Map>
                )}
            </div>
        </div>
    )
}
